import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { CHIP_SEVERITY } from "@/components/ui/WarnChip";
import { canRecalcRouteState } from "@/features/route/routeStaleness";
import { formatRunLabel, isNewerRun } from "@/features/weatherRun/weatherRunModel";
import { PANEL_BUTTON_SX } from "@/theme/panelStyles";

dayjs.extend(utc);

const TONES = {
  stale: {
    bg: CHIP_SEVERITY.error.bgcolor,
    border: CHIP_SEVERITY.error.border,
    color: CHIP_SEVERITY.error.color,
    icon: CloudOffOutlinedIcon,
  },
  fresh: {
    bg: CHIP_SEVERITY.success.bgcolor,
    border: CHIP_SEVERITY.success.border,
    color: CHIP_SEVERITY.success.color,
    icon: CloudDoneOutlinedIcon,
  },
  unknown: {
    bg: "#f1f4f7",
    border: "1px solid #e2e8ee",
    color: "#57728d",
    icon: CloudQueueOutlinedIcon,
  },
};

const UPDATE_BUTTON_SX = {
  ...PANEL_BUTTON_SX,
  backgroundColor: "#b23a2d",
  color: "#fff",
  flexShrink: 0,
  "&:hover": { backgroundColor: "#9a3125" },
  "&.Mui-disabled": { backgroundColor: "#b23a2d", color: "#fff", opacity: 0.7 },
};

function formatRunDate(iso) {
  const d = dayjs.utc(iso);
  return d.isValid() ? `${d.format("DD.MM.YYYY HH:mm")} UTC` : null;
}

function bannerText(staleness, status) {
  const calcDate = staleness?.calculatedAt ? formatRunDate(staleness.calculatedAt) : null;
  if (status === "unknown") return { title: "Weather data: age unknown", detail: null };

  const basis = staleness.basis;
  const current = staleness.currentRun ? formatRunLabel(staleness.currentRun) : null;
  if (basis === "run" || basis === "activation") {
    const used = staleness.routeRun ? `run of ${formatRunLabel(staleness.routeRun)}` : null;
    const since = staleness.activatedAt ? formatRunDate(staleness.activatedAt) : null;
    if (status === "fresh") {
      return {
        title: "Weather data current",
        detail: used
          ? `Computed with the ${used}`
          : `Computed after the switch to the run of ${current}`,
      };
    }
    const rolledBack = used && !isNewerRun(staleness.currentRun, staleness.routeRun);
    const currentNote = `The current forecast (run ${current}) is available${since ? ` since ${since}` : ""}.`;
    return {
      title: rolledBack ? "Weather forecast changed" : "Newer weather forecast available",
      detail: used
        ? `Route was computed with the ${used}; current: run of ${current}.`
        : `Route was computed${calcDate ? ` on ${calcDate}` : ""}. ${currentNote}`,
    };
  }

  if (status === "fresh") {
    return { title: "Weather data current", detail: calcDate ? `Computed on ${calcDate}` : null };
  }
  return {
    title: staleness.ageHours != null ? `Weather data ${staleness.ageHours} h old` : "Weather data outdated",
    detail: calcDate ? `Route was computed on ${calcDate}` : null,
  };
}

// `blocked`: another route is recalculating (one at a time) — keep the button
// visible but disabled instead of dropping the click silently.
export default function WeatherBanner({
  staleness,
  routeState,
  onUpdate,
  updating = false,
  blocked = false,
}) {
  const status = staleness?.status ?? "unknown";
  const tone = TONES[status] ?? TONES.unknown;
  const Icon = tone.icon;
  const canUpdate = status === "stale" && canRecalcRouteState(routeState);
  const { title, detail } = bannerText(staleness, status);

  return (
    <Box
      data-testid="weather-banner"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 0.75,
        borderRadius: "10px",
        bgcolor: tone.bg,
        border: tone.border,
        color: tone.color,
      }}
    >
      <Icon sx={{ fontSize: 18, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 700, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {detail ? (
          <Typography sx={{ fontSize: "11px", lineHeight: 1.3, opacity: 0.85 }}>
            {detail}
          </Typography>
        ) : null}
      </Box>
      {canUpdate ? (
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={updating || blocked}
          onClick={(e) => {
            e.stopPropagation();
            onUpdate?.();
          }}
          startIcon={
            updating ? (
              <CircularProgress size={12} thickness={5} sx={{ color: "#fff" }} />
            ) : (
              <RefreshIcon sx={{ fontSize: 14 }} />
            )
          }
          sx={UPDATE_BUTTON_SX}
        >
          {updating ? "Updating …" : "Update"}
        </Button>
      ) : null}
    </Box>
  );
}
